import { runConversation, type ChatTurn } from './providers';
import { loadConfig, saveConfig, type AIConfig } from './storage';

const EXTERNAL_SYSTEM_PROMPT = `You are driving Float:Flash-html5 (https://flash.float.do) — a static, browser-only editor for short promo videos and card-news. Authoring happens by calling tools; the editor renders the resulting Project state.

Available tools (via MCP server "promo-studio" or via window.promoStudio.callTool):
- get_project — read canvas + layers + audio
- add_text_layer / add_shape_layer
- add_image_layer_from_file / add_video_layer_from_file (after attach_file)
- update_layer / delete_layer / duplicate_layer / select_layer
- set_background / set_canvas
- attach_file / list_files / set_background_from_file
- set_audio_youtube / clear_audio / fetch_youtube_captions
- get_studio_url — encode current project as a ?project=… URL the user can open

Conventions:
- Coordinates are CANVAS COORDS, center-anchored. 9:16 = 1080×1920 (center 540, 960). Times in ms.
- Default duration ~5–10s. Headlines fontSize 80–160 / weight 800–900. Body 36–60.
- Korean-friendly default font is "Pretendard"; you don't need to set it explicitly.
- Animations: fade, slide, pop (impact), typing (text reveal), strike (strikethrough).
- Reply in the user's language. Be brief — 1–2 lines after each action.

Workflow:
1. If unsure about state, call get_project first.
2. Build the scene with a few layers + a background.
3. (Optional) For background music: call fetch_youtube_captions to find lyric segments that fit the message, then set_audio_youtube with songStart/songEnd.
4. Finish with get_studio_url and hand the URL to the user.`;

export class AssistantPanel {
  private cfg: AIConfig;
  private history: ChatTurn[] = [];
  private isOpen = false;
  private busy = false;

  // refs
  private wrap!: HTMLElement;
  private toggleBtn!: HTMLButtonElement;
  private toggleGroup!: HTMLElement;
  private settingsArea!: HTMLElement;
  private chatArea!: HTMLElement;
  private inputEl!: HTMLTextAreaElement;
  private statusEl!: HTMLElement;

  constructor(_parent: HTMLElement) {
    this.cfg = loadConfig();
    this.build();
  }

  private build(): void {
    this.wrap = document.createElement('aside');
    this.wrap.className = 'assistant-panel';

    const header = document.createElement('header');
    header.className = 'a-header';
    const title = document.createElement('div');
    title.className = 'a-title';
    title.textContent = '🤖 AI 어시스턴트';
    const close = document.createElement('button');
    close.className = 'a-close';
    close.textContent = '✕';
    close.addEventListener('click', () => this.toggle(false));
    header.append(title, close);

    this.settingsArea = document.createElement('section');
    this.settingsArea.className = 'a-settings';
    this.buildSettings();

    this.chatArea = document.createElement('section');
    this.chatArea.className = 'a-chat';

    const inputBar = document.createElement('div');
    inputBar.className = 'a-input';
    this.inputEl = document.createElement('textarea');
    this.inputEl.placeholder = '예: "어린이날 카드뉴스 만들어줘. 빨간 강조 한 줄 넣고 ADHD 글자가 팡 튀게."';
    this.inputEl.rows = 2;
    this.inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        this.send();
      }
    });
    const sendBtn = document.createElement('button');
    sendBtn.className = 'a-send';
    sendBtn.textContent = '보내기 (⌘↵)';
    sendBtn.addEventListener('click', () => this.send());
    inputBar.append(this.inputEl, sendBtn);

    this.statusEl = document.createElement('div');
    this.statusEl.className = 'a-status';

    this.wrap.append(header, this.settingsArea, this.chatArea, this.statusEl, inputBar);
    document.body.appendChild(this.wrap);

    // toggle FAB on the right side of the screen, with a label above
    const fabGroup = document.createElement('div');
    fabGroup.className = 'assistant-fab-group';
    const fabLabel = document.createElement('div');
    fabLabel.className = 'assistant-fab-label';
    fabLabel.textContent = 'AI 어시스턴트';
    this.toggleBtn = document.createElement('button');
    this.toggleBtn.className = 'assistant-fab';
    this.toggleBtn.textContent = '🤖';
    this.toggleBtn.title = 'AI 어시스턴트 열기';
    this.toggleBtn.addEventListener('click', () => this.toggle(!this.isOpen));
    fabGroup.append(fabLabel, this.toggleBtn);
    document.body.appendChild(fabGroup);
    this.toggleGroup = fabGroup;

    this.renderChat();
  }

  private buildSettings(): void {
    this.settingsArea.replaceChildren();

    const notice = document.createElement('div');
    notice.className = 'a-notice';
    notice.innerText =
      '🛡 이 사이트는 스태틱 — 서버 없음.\n' +
      'API 키는 너의 브라우저에서만 보관되고, OpenAI/Anthropic으로 직접 보내져. 우리한테 안 와.\n' +
      '체크박스 끄면 새로고침할 때 키가 사라져.';

    const externalNotice = document.createElement('details');
    externalNotice.className = 'a-external';
    const externalSummary = document.createElement('summary');
    externalSummary.textContent = '🔌 키 없이 외부 AI로 컨트롤 (Claude Desktop · MCP)';
    externalNotice.appendChild(externalSummary);

    const expl = document.createElement('div');
    expl.className = 'a-external-body';

    const explText = document.createElement('p');
    explText.textContent =
      'Claude Desktop의 MCP나 다른 AI 클라이언트가 이 페이지를 키 입력 없이 직접 조작할 수 있어요. ' +
      '여기 패널의 키는 페이지 안에서 직접 채팅할 때만 필요하고, 외부 AI는 자기 구독을 써요.';
    expl.appendChild(explText);

    // ─── System prompt FIRST so users can grab it without scrolling ───
    const promptHeader = document.createElement('p');
    promptHeader.textContent = '외부 AI에 줄 시스템 프롬프트 (그대로 복사해서 써):';
    promptHeader.style.marginTop = '6px';

    const promptArea = document.createElement('textarea');
    promptArea.value = EXTERNAL_SYSTEM_PROMPT;
    promptArea.readOnly = true;
    promptArea.rows = 8;
    promptArea.className = 'a-prompt';

    const copyBtn = document.createElement('button');
    copyBtn.className = 'btn ghost a-copy';
    copyBtn.textContent = '시스템 프롬프트 복사';
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(EXTERNAL_SYSTEM_PROMPT);
        copyBtn.textContent = '✅ 복사됨';
        setTimeout(() => (copyBtn.textContent = '시스템 프롬프트 복사'), 1200);
      } catch {
        copyBtn.textContent = '⚠ 복사 실패';
      }
    });

    expl.append(promptHeader, promptArea, copyBtn);

    const ways = document.createElement('p');
    ways.textContent = '연결 방법:';
    ways.style.marginTop = '10px';
    expl.appendChild(ways);

    const methods: { title: string; lines: (string | { link: string; href: string })[] }[] = [
      {
        title: '① Claude Desktop · MCP 서버',
        lines: [
          'Claude Desktop을 설치 (',
          { link: 'claude.ai/download', href: 'https://claude.ai/download' },
          ') → 이 저장소 mcp-server/ 빌드 → claude_desktop_config.json 의 mcpServers에 등록 → 재시작.',
          '도구 자동으로 잡히고 키 없이 작동해요.',
        ],
      },
      {
        title: '② Chrome 확장 (MCP-B / postMessage 어댑터)',
        lines: [
          '크롬 웹스토어 검색: ',
          { link: 'MCP-B', href: 'https://chromewebstore.google.com/search/MCP-B' },
          ' 또는 ',
          { link: 'postMessage 어댑터', href: 'https://chromewebstore.google.com/search/postmessage' },
          ' — 설치 후 이 탭이 열려있으면 확장이 window.postMessage 로 직접 도구 호출.',
          '이 페이지가 받는 메시지: { kind:"promo-studio", action, ... }',
        ],
      },
      {
        title: '③ ChatGPT · Custom GPT / Connector',
        lines: [
          'ChatGPT에서 새 GPT 생성 → Actions → 이 페이지 도메인을 허용 → 가져온 OpenAPI 스펙 또는 위 시스템 프롬프트로 호출.',
          '직접 API key 없이 자기 ChatGPT 구독으로 작업.',
        ],
      },
      {
        title: '④ URL 파라미터',
        lines: [
          '?prompt=... 로 채팅 자동 입력, ?project=<base64> 로 .float JSON 자동 적용.',
          '북마클릿이나 다른 사이트 링크에서 바로 호출.',
        ],
      },
      {
        title: '⑤ window.postMessage (직접 코딩)',
        lines: [
          '같은 브라우저의 다른 탭/창에서:',
          'window.opener.postMessage({ kind:"promo-studio", action:"prompt", text:"..." }, "*")',
        ],
      },
    ];

    for (const m of methods) {
      const card = document.createElement('div');
      card.className = 'a-method-card';
      const t = document.createElement('div');
      t.className = 'a-method-title';
      t.textContent = m.title;
      card.appendChild(t);
      const body = document.createElement('div');
      body.className = 'a-method-body';
      for (const piece of m.lines) {
        if (typeof piece === 'string') {
          body.appendChild(document.createTextNode(piece));
        } else {
          const a = document.createElement('a');
          a.href = piece.href;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          a.textContent = piece.link;
          body.appendChild(a);
        }
      }
      card.appendChild(body);
      expl.appendChild(card);
    }

    externalNotice.appendChild(expl);

    const providerRow = labeledRow('프로바이더');
    const providerSel = document.createElement('select');
    for (const opt of ['anthropic', 'openai'] as const) {
      const o = document.createElement('option');
      o.value = opt;
      o.textContent = opt === 'anthropic' ? 'Anthropic (Claude)' : 'OpenAI (GPT)';
      providerSel.appendChild(o);
    }
    providerSel.value = this.cfg.provider;
    providerSel.addEventListener('change', () => {
      this.cfg = { ...this.cfg, provider: providerSel.value as AIConfig['provider'] };
      saveConfig(this.cfg);
      keyHint.textContent = providerSel.value === 'anthropic' ? 'sk-ant-...' : 'sk-...';
      // reset history when switching providers (formats differ internally)
      this.history = [];
      this.renderChat();
    });
    providerRow.append(providerSel);

    const keyRow = labeledRow('API 키');
    const keyInput = document.createElement('input');
    keyInput.type = 'password';
    keyInput.value = this.cfg.apiKey;
    const keyHint = document.createElement('span');
    keyHint.className = 'a-hint';
    keyHint.textContent = this.cfg.provider === 'anthropic' ? 'sk-ant-...' : 'sk-...';
    keyInput.placeholder = keyHint.textContent;
    keyInput.addEventListener('input', () => {
      this.cfg = { ...this.cfg, apiKey: keyInput.value };
      saveConfig(this.cfg);
    });
    keyRow.append(keyInput, keyHint);

    const rememberRow = document.createElement('label');
    rememberRow.className = 'a-remember-row';
    const rememberLabel = document.createElement('span');
    rememberLabel.className = 'a-remember-label';
    rememberLabel.textContent = '새로고침해도 키 기억하기';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.className = 'a-remember-cb';
    cb.checked = this.cfg.remember;
    cb.addEventListener('change', () => {
      this.cfg = { ...this.cfg, remember: cb.checked };
      saveConfig(this.cfg);
    });
    rememberRow.append(rememberLabel, cb);

    const poweredBy = document.createElement('div');
    poweredBy.className = 'a-powered-by';
    poweredBy.append(document.createTextNode('powered by '));
    const ridm = document.createElement('a');
    ridm.href = 'https://ridm.ai';
    ridm.target = '_blank';
    ridm.rel = 'noopener noreferrer';
    ridm.textContent = 'ridm.ai';
    poweredBy.appendChild(ridm);

    this.settingsArea.append(notice, externalNotice, providerRow, keyRow, rememberRow, poweredBy);
  }

  private renderChat(): void {
    this.chatArea.replaceChildren();
    if (this.history.length === 0) {
      const hint = document.createElement('div');
      hint.className = 'a-empty';
      hint.innerText =
        '예시:\n' +
        '· "어린이날 카드뉴스 톤으로 다시 만들어줘"\n' +
        '· "5월 5일을 더 크게, ADHD는 빨간색으로"\n' +
        '· "배경을 어두운 그라디언트로 바꿔줘"\n' +
        '· "지금 길이 너무 짧아 — 12초로 늘리고 마지막에 클로징 한 줄 추가"';
      this.chatArea.appendChild(hint);
      return;
    }
    for (const turn of this.history) {
      const m = document.createElement('div');
      m.className = `a-msg a-${turn.role}`;
      m.textContent = turn.text;
      this.chatArea.appendChild(m);
    }
    this.chatArea.scrollTop = this.chatArea.scrollHeight;
  }

  private async send(): Promise<void> {
    if (this.busy) return;
    const text = this.inputEl.value.trim();
    if (!text) return;
    this.cfg = loadConfig();
    if (!this.cfg.apiKey) {
      this.setStatus('API 키를 입력해주세요', true);
      return;
    }
    this.busy = true;
    this.inputEl.value = '';
    this.history = [...this.history, { role: 'user', text }];
    this.renderChat();
    this.setStatus('생각 중…', false);

    let liveText = '';
    const placeholder: ChatTurn = { role: 'assistant', text: '…' };
    this.history.push(placeholder);
    this.renderChat();

    try {
      const updated = await runConversation({
        provider: this.cfg.provider,
        apiKey: this.cfg.apiKey,
        history: this.history.slice(0, -2), // exclude just-added user + placeholder
        userMessage: text,
        onText: (chunk) => {
          liveText += chunk;
          placeholder.text = liveText;
          this.renderChat();
        },
        onTool: (info) => {
          this.setStatus(
            `🔧 ${info.name}${info.ok ? '' : ' ⚠ ' + (info.detail ?? 'failed')}`,
            !info.ok,
          );
        },
        onModel: (m) => this.setStatus(`모델: ${m}`, false),
      });
      this.history = updated;
      this.renderChat();
      this.setStatus('완료', false);
    } catch (err) {
      this.history.pop(); // remove placeholder
      const message = err instanceof Error ? err.message : String(err);
      this.history.push({ role: 'assistant', text: `⚠ ${message}` });
      this.renderChat();
      this.setStatus('에러', true);
    } finally {
      this.busy = false;
    }
  }

  private setStatus(text: string, isError: boolean): void {
    this.statusEl.textContent = text;
    this.statusEl.classList.toggle('error', isError);
  }

  toggle(open: boolean): void {
    this.isOpen = open;
    this.wrap.classList.toggle('open', open);
    this.toggleGroup.classList.toggle('hide', open);
    if (open) {
      this.cfg = loadConfig();
      this.buildSettings();
    }
  }

  /** Used by the external control API to run a prompt programmatically. */
  runPrompt(prompt: string): void {
    this.toggle(true);
    this.inputEl.value = prompt;
    this.inputEl.focus();
    void this.send();
  }
}

function labeledRow(label: string): HTMLElement {
  const r = document.createElement('div');
  r.className = 'a-row';
  const l = document.createElement('span');
  l.className = 'a-label';
  l.textContent = label;
  r.appendChild(l);
  return r;
}
